/**
 * Search Page
 * Searches everything — users (by name/username) and posts (by content/hashtag).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { capture } from '../utils/analytics';
import SinglePostDisplay from '../components/post/SinglePostDisplay';
import './SearchUsersPage.css';

const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);

  const runSearch = useCallback(async () => {
    if (!query.trim()) {
      setUsers([]);
      setPosts([]);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get('/api/search', { params: { q: query } });
      setUsers(response.data.users || []);
      setPosts(response.data.posts || []);
      capture('search_performed', {
        resultCount: (response.data.users?.length || 0) + (response.data.posts?.length || 0)
      });
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    runSearch();
  }, [runSearch]);

  const total = users.length + posts.length;

  return (
    <div className="container">
      <h1>Search</h1>
      {query && (
        <p className="search-results-info">
          {loading ? 'Searching...' : `Found ${total} result${total !== 1 ? 's' : ''} for "${query}"`}
        </p>
      )}

      {!query && (
        <div className="no-query">
          <p>Enter a search term in the navbar to search people and posts</p>
        </div>
      )}

      {loading && <div className="loading">Searching...</div>}

      {!loading && query && total === 0 && (
        <div className="no-results">
          <p>Nothing found matching your search.</p>
        </div>
      )}

      {!loading && users.length > 0 && (
        <section className="search-section">
          <h2>People</h2>
          <div className="users-list">
            {users.map((user) => (
              <Link key={user._id} to={`/profile/${user._id}`} className="user-card">
                {user.avatar ? (
                  <img src={user.avatar} alt={user.username} className="user-avatar" />
                ) : (
                  <div className="user-avatar-placeholder">
                    {user.username?.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="user-info">
                  <div className="user-name">{user.fullName || user.username}</div>
                  <div className="user-username">@{user.username}</div>
                  {user.bio && <div className="user-bio">{user.bio}</div>}
                </div>
                <div className="user-stats">
                  <div className="stat">
                    <span className="stat-number">{user.followers?.length || 0}</span>
                    <span className="stat-label">Followers</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {!loading && posts.length > 0 && (
        <section className="search-section">
          <h2>Posts</h2>
          <div className="posts-list">
            {posts.map((post) => (
              <SinglePostDisplay key={post._id} post={post} onUpdate={runSearch} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default SearchPage;
